/**
 * Free Destination Guide download card.
 *
 * Rendered on every tour page below the description and above the booking
 * widget. Links to the public PDF endpoint with the visitor's active language.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpen, Download, Loader2, Printer, ShieldCheck, CreditCard } from "lucide-react";

interface Props {
  slug: string;
  destinationName: string;
}

type Lang = "es" | "en" | "fr" | "pt" | "it";

const LABELS: Record<Lang, {
  titlePre: string; titleHighlight: string; titlePost: string;
  desc: string; cta: string; downloading: string;
  noSignup: string; noPayment: string; printReady: string;
}> = {
  es: {
    titlePre: "Guía ", titleHighlight: "Gratuita", titlePost: " de Descarga",
    desc: "Llévate gratis una guía completa de este destino con información útil para disfrutar tu viaje al máximo.",
    cta: "Descargar guía", downloading: "Generando guía…",
    noSignup: "Sin registro", noPayment: "Sin pago", printReady: "Lista para imprimir",
  },
  en: {
    titlePre: "", titleHighlight: "Free", titlePost: " Download Guide",
    desc: "Take home a complete guide of this destination with helpful information to make the most of your trip.",
    cta: "Download guide", downloading: "Generating guide…",
    noSignup: "No sign-up", noPayment: "No payment", printReady: "Ready to print",
  },
  fr: {
    titlePre: "Guide de téléchargement ", titleHighlight: "gratuit", titlePost: "",
    desc: "Emporte un guide complet et gratuit de cette destination, avec des infos utiles pour ton voyage.",
    cta: "Télécharger le guide", downloading: "Génération du guide…",
    noSignup: "Sans inscription", noPayment: "Sans paiement", printReady: "Prêt à imprimer",
  },
  pt: {
    titlePre: "Guia ", titleHighlight: "Gratuito", titlePost: " para Baixar",
    desc: "Leve grátis um guia completo deste destino com informações úteis para aproveitar sua viagem ao máximo.",
    cta: "Baixar guia", downloading: "Gerando o guia…",
    noSignup: "Sem cadastro", noPayment: "Sem pagamento", printReady: "Pronto para imprimir",
  },
  it: {
    titlePre: "Guida ", titleHighlight: "Gratuita", titlePost: " da Scaricare",
    desc: "Porta con te una guida completa e gratuita di questa destinazione, con informazioni utili per goderti al meglio il tuo viaggio.",
    cta: "Scarica la guida", downloading: "Generazione della guida…",
    noSignup: "Senza registrazione", noPayment: "Senza pagamento", printReady: "Pronta da stampare",
  },
};

function pickLang(raw: string): Lang {
  const v = (raw || "").slice(0, 2).toLowerCase();
  if (v === "en" || v === "fr" || v === "pt" || v === "it") return v;
  return "es";
}

function buildFilename(name: string, lang: Lang): string {
  const slug = name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  switch (lang) {
    case "en": return `mostrando-mexico-guide-${slug}-en.pdf`;
    case "fr": return `guide-mostrando-mexique-${slug}-fr.pdf`;
    case "pt": return `guia-mostrando-mexico-${slug}-pt.pdf`;
    case "it": return `guida-mostrando-mexico-${slug}-it.pdf`;
    case "es":
    default:   return `guia-mostrando-mexico-${slug}-es.pdf`;
  }
}

export function FreeDestinationGuideDownload({ slug, destinationName }: Props) {
  const { i18n } = useTranslation();
  const lang = pickLang(i18n.language);
  const labels = LABELS[lang];
  const [busy, setBusy] = useState(false);

  async function handleDownload() {
    setBusy(true);
    try {
      const url = `/api/public/destination-guide/pdf?slug=${encodeURIComponent(slug)}&lang=${lang}`;
      // Fetch as blob so we can show progress and trigger a clean download
      // (server route already sets Content-Disposition).
      // The server returns 503 while the guide is still being fully
      // translated into the active language; retry once.
      let resp = await fetch(url, { credentials: "omit" });
      if (resp.status === 503) {
        await new Promise((r) => setTimeout(r, 1500));
        resp = await fetch(url, { credentials: "omit" });
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = buildFilename(destinationName, lang);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    } catch (e) {
      console.error("[destination-guide-download]", e);
      // Fallback: open in a new tab, server response will trigger download
      window.open(
        `/api/public/destination-guide/pdf?slug=${encodeURIComponent(slug)}&lang=${lang}`,
        "_blank",
        "noopener",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-labelledby="free-guide-heading"
      className="mt-10 rounded-[20px] border border-border/60 bg-white p-6 shadow-[0_10px_30px_-18px_rgba(15,23,42,0.18)] dark:bg-slate-900"
    >
      <div className="flex items-start gap-4">
        <div
          className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl text-white shadow-[0_8px_20px_-8px_rgba(242,96,0,0.55)]"
          style={{ background: "linear-gradient(135deg, #FF5A00 0%, #FF8A00 100%)" }}
        >
          <BookOpen className="h-7 w-7" strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <h2
            id="free-guide-heading"
            className="font-display text-[1.6rem] font-bold leading-[1.15] tracking-tight text-foreground"
          >
            {labels.titlePre}
            <span className="text-primary">{labels.titleHighlight}</span>
            {labels.titlePost}
          </h2>
          <div className="mt-2 h-[3px] w-14 rounded-full bg-primary" />
        </div>
      </div>

      <p className="mt-5 text-[0.95rem] leading-[1.55] text-foreground/80">
        {labels.desc}
      </p>

      <button
        type="button"
        onClick={handleDownload}
        disabled={busy}
        className="group mt-5 flex h-[52px] w-full items-center justify-center gap-2.5 rounded-2xl text-[0.95rem] font-semibold text-white shadow-[0_10px_25px_-10px_rgba(242,96,0,0.55)] transition-all duration-250 hover:-translate-y-0.5 hover:shadow-[0_16px_32px_-12px_rgba(242,96,0,0.6)] disabled:cursor-wait disabled:opacity-70"
        style={{ background: "linear-gradient(135deg, #FF5A00 0%, #FF8A00 100%)" }}
      >
        {busy ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" /> {labels.downloading}
          </>
        ) : (
          <>
            <Download className="h-5 w-5" strokeWidth={2.4} /> {labels.cta}
          </>
        )}
      </button>

      <div className="mt-5 grid grid-cols-3 gap-2 border-t border-border/50 pt-4 text-[0.72rem] font-medium text-foreground/75">
        <div className="flex items-center justify-center gap-1.5 text-center">
          <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
          <span>{labels.noSignup}</span>
        </div>
        <div className="flex items-center justify-center gap-1.5 border-x border-border/50 text-center">
          <CreditCard className="h-4 w-4 shrink-0 text-primary" />
          <span>{labels.noPayment}</span>
        </div>
        <div className="flex items-center justify-center gap-1.5 text-center">
          <Printer className="h-4 w-4 shrink-0 text-primary" />
          <span>{labels.printReady}</span>
        </div>
      </div>
    </section>
  );
}
